import { Box, Button, Typography } from "@mui/material";
import { ErrorOutline, Refresh } from "@mui/icons-material";

import React from "react";

interface IProps {
    children: React.ReactNode;
}

interface IState {
    hasError: boolean;
}

/**
 * Captura erros de renderização em qualquer tela e exibe uma mensagem amigável
 * no lugar da tela branca. Sem este boundary, qualquer exceção durante o render
 * (ex.: dropdown que recebe `options` undefined quando o backend está fora /
 * cold start) derruba toda a árvore React e força o usuário a recarregar.
 */
class ErrorBoundary extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(): IState {
        return { hasError: true };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        // Mantém o erro no console para depuração, mas sem quebrar a UI.
        console.error("Erro de renderização capturado pelo ErrorBoundary:", error, info);
    }

    handleReset = () => {
        this.setState({ hasError: false });
    };

    render() {
        if (this.state.hasError) {
            return (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        gap: 2,
                        height: "100%",
                        minHeight: "60vh",
                        p: 4,
                    }}
                >
                    <ErrorOutline sx={{ fontSize: 64, color: "#E16E32" }} />
                    <Typography variant="h6" color="textPrimary">
                        Ocorreu um erro ao carregar esta tela
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 480 }}>
                        Pode ser uma instabilidade temporária no serviço. Tente novamente em instantes.
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                        <Button
                            variant="contained"
                            color="warning"
                            startIcon={<Refresh />}
                            onClick={this.handleReset}
                            disableElevation
                        >
                            Tentar novamente
                        </Button>
                        <Button variant="outlined" color="warning" onClick={() => window.location.reload()} disableElevation>
                            Recarregar página
                        </Button>
                    </Box>
                </Box>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
